<template>
  <div class="product-thumbnails preserve-cursor">
    <div
      ref="productThumbnails"
      class="product-thumbnails__scroll-wrapper"
    >
      <template
        v-for="(image, index) of images"
        :key="index"
      >
        <button
          v-if="image?.media_type !== 'video' && image?.mediaContentType !== 'VIDEO'"
          class="product-thumbnails__wrapper"
          :class="{
            [$class('active')]: index === imageIndex
          }"
          :js-product-thumbnail="index"
          type="button"
          @click="handleImageClick(index)"
        >
          <art-direction
            block-class="product-thumbnails"
            :image="image"
            class="product-thumbnails__thumbnail"
            ratio="image"
          />
        </button>

        <button
          v-else-if="
            image?.media_type === 'video' || image.mediaContentType === 'VIDEO'
          "
          class="product-thumbnails__wrapper"
          :class="{
            [$class('active')]: index === imageIndex
          }"
          :js-product-thumbnail="index"
          type="button"
          @click="() => handleImageClick(index)"
        >
          <art-direction
            v-if="image.preview_image?.src"
            block-class="product-thumbnails"
            :image="{
              alt: image.alt || $string('accessibility.image_placeholder'),
              aspectRatio: 0.71,
              src: image.preview_image.src
            }"
            class="product-thumbnails__thumbnail"
            ratio="image"
          />
        </button>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product thumbnails (product-thumbnails)
 * -----------------------------------------------------------------------------
 * Component that manages the display and functionality of product thumbnails.
 *
 * @param {Boolean} galleryExpanded - Whether the gallery is in the zoomed state
 * or not.
 * @param {Array} images - Array of product images.
 * @param {Number} imageIndex - Index of the presently selected image.
 *
 */
import ArtDirection from '~global/images/art-direction'

export default {
  name: 'ProductThumbnails',

  components: {
    ArtDirection,
  },

  props: {
    images: {
      type: Array,
      default: () => [],
    },

    imageIndex: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'thumbnail-clicked': (payload) => typeof payload === 'number',
  },

  watch: {

    /**
     * When images are all available, calculate the total height.
     */
    images: {
      handler() {
        this.getTrackHeight()
      },
    },

    /**
     * When the image index changes, scroll it into view.
     */
    imageIndex(value) {

      if (Number.isNaN(value)) {
        return
      }
      this.scrollThumbnailIntoView()
    },
  },

  methods: {

    /**
     * Ensure active thumbnails are always in view.
     */
    scrollThumbnailIntoView() {
      const element = document.querySelector(`[js-product-thumbnail="${this.imageIndex}"]`)
      this.$refs.productThumbnails.scrollTo({ top: element.offsetTop, behavior: 'smooth' })
    },

    /**
     * Calculates the thumbnail wrapper height and creates a css variable with
     * the value.
     */
    async getTrackHeight() {
      let elapsed = 0
      let images = []
      let height = 0

      await new Promise((resolve) => {
        const thumbnailLoad = window.setInterval(() => {
          images = [...document.querySelectorAll('.product-thumbnails__wrapper')]
          if (!images.length && elapsed <= 5000) {
            elapsed += 150
            return
          }

          window.clearInterval(thumbnailLoad)
          resolve()
        }, this.$timing('quick'))
      })

      if (images.length < 5) {
        height = images.reduce((agg, item) => {
          // eslint-disable-next-line no-param-reassign
          agg += item.offsetHeight
          return agg
        }, 0)
      } else {
        height = images.slice(0, 5).reduce((agg, item) => {
          // eslint-disable-next-line no-param-reassign
          agg += item.offsetHeight
          return agg
        }, 0)
      }

      document.documentElement.style.setProperty(
        '--thumbnails-visible-height',
        `${height}px`,
      )
    },

    /**
     * Handles clicks on an image thumbnail.
     * @param {Number} index - Index of the image clicked.
     */
    handleImageClick(index) {
      this.$emit('thumbnail-clicked', index)
    },
  },
}
</script>

<style lang="scss">
@import './product-thumbnails';
</style>
